import Loader from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchSingleEnrollments,
  updateEnrollment,
} from "@/store/features/enroll-slice";
import { dispatch, useSelector } from "@/store/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { formSchema, FormValues } from "../schema/form-schema";

const AddEnrollmentPage = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchLoading = useSelector((state) => state.enrollment.fetchLoading);
  const loading = useSelector((state) => state.enrollment.isLoading);
  const singleEnrollment = useSelector((state) => state.enrollment.singleData);
  const courses = useSelector((state) => state.course.data)?.data;

  // TODO: fetch the single enrollment
  useEffect(() => {
    if (isEdit && id) {
      dispatch(
        fetchSingleEnrollments({
          id,
          params: {
            populate: "course",
          },
        })
      );
    }
  }, [id, isEdit]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      mobile: "",
      address: "",
      academicLevel: "",
      message: "",
      course: "",
    },
  });

  const onSubmit = (data: FormValues) => {
    dispatch(
      updateEnrollment({
        data,
        navigate,
        id,
      })
    );
  };

  useEffect(() => {
    if (singleEnrollment && id) {
      form.reset({
        fullName: singleEnrollment?.fullName || "",
        email: singleEnrollment?.email || "",
        mobile: singleEnrollment?.mobile || "",
        address: singleEnrollment?.address || "",
        academicLevel: singleEnrollment?.academicLevel || "",
        message: singleEnrollment?.message || "",
        course: singleEnrollment?.course?.id || "",
      });
    }
  }, [singleEnrollment, form, id]);

  return (
    <>
      {fetchLoading ? (
        <Loader />
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="flex gap-10 flex-wrap">
              <Card className="my-6 p-2 sm:p-6 flex-[2]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-20">
                    Update Enrollment of {singleEnrollment?.studentName}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }: { field: any }) => (
                        <FormItem>
                          <FormLabel>Student Name *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              {/* <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                className="pl-3"
                                placeholder="Eg: Saroj Dangol"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }: { field: any }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              {/* <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                type="email"
                                className="pl-3"
                                placeholder="Eg: example@gmail.com"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="mobile"
                      render={({ field }: { field: any }) => (
                        <FormItem>
                          <FormLabel>Mobile *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              {/* <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                className="pl-3"
                                placeholder="Eg: 9863485599"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }: { field: any }) => (
                        <FormItem>
                          <FormLabel>Address *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              {/* <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                className="pl-3"
                                placeholder="Eg: Lalitpur"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="academicLevel"
                      render={({ field }: { field: any }) => (
                        <FormItem>
                          <FormLabel>Academic Level *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              {/* <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                className="pl-3"
                                placeholder="Eg: Bachelor"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="course"
                      render={({
                        field,
                      }: {
                        field: {
                          onChange: (value: string) => void;
                          value: string;
                        };
                      }) => (
                        <FormItem>
                          <FormLabel>Course *</FormLabel>
                          <Select
                            key={field.value}
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select Course" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {courses?.map((c: any, i: number) => (
                                <SelectItem key={i} value={c?.id}>
                                  {c?.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <div className="relative">
                            {/* <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /> */}
                            <Textarea
                              className="pl-3"
                              placeholder="Eg: Message"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2">
              <Button
                disabled={loading}
                type="button"
                variant="outline"
                onClick={() => navigate("/enrollments")}
              >
                Cancel
              </Button>
              <Button disabled={loading} type="submit">
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Please wait
                  </>
                ) : (
                  <>Update</>
                )}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
};

// Simple check icon component

export default AddEnrollmentPage;
